import React, { Component } from 'react';
import shortid from 'shortid';
import initReactFastclick from 'react-fastclick';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { isNil } from 'lodash';
import { isRunningInSEBBrowser, openSecureBrowser } from '@amplify/secure-exam-browser';
import AmplifyButton from '@amplify/amplify-button';
import classNames from 'classnames';
import {
    initPreventDoubleTapZoom,
    destroyPreventDoubleTapZoom,
} from './common/utils/preventDoubleTapZoom';
import HeaderContainer from './common/header/HeaderContainer';
import AvatarContainer from './common/avatar/avatarContainer';
import SvgSprite from './common/components/SvgSprite';
import SessionTrackerContainer from './common/user/SessionTrackerContainer';
import isAccessibilityEnabled from './common/utils/commonUtils';
import AccessibilitySettingsDropdown from './common/components/AccessibilitySettingsDropdown/AccessibilitySettingsDropdown';
import AmpNavigationContainer from './common/header/AmpNavigationContainer';
import { LANDING_PAGES, PAGES_WITH_MAIN_NAVIGATION_AND_SUBHEADER} from './constants/url';
import LoadingPage from './common/components/LoadingPage';
import LogoutModalContainer from './common/logout/LogoutModalContainer';
import AppErrorContainer from './common/error/AppErrorContainer';
import { SUPPORTED_LANGUAGES } from './constants/language';
import AudioSavingErrorContainer from './common/error/AudioSavingErrorContainer';
import Alex from './common/alex/Alex';
import DeviceRotateAnimation from "./common/animation/DeviceRotateAnimation";
import Captions from './common/captions/Captions';
import { SEB_URL, STUDENT_HOME } from "./constants/secureBrowser";
import '@amplify/amplify-styles/dist/assets/toolkit/styles-release/amp-styles.css';
import { setArPageTitle } from './trc/troUtils';
import keycloak from "./common/keycloak/keycloak";
import { ApolloContext } from './ApolloContextProvider';
import { handleCloseSEB, isKioskChromeOS, storeKioskModeParameter, isChromeOS } from './common/utils/browserUtils';
import { LANGUAGES } from './common/localization/consts';

class App extends Component {
    constructor(props) {
        super(props);
        this.state = {
            startingSecureBrowser: false,
            showContent: true,
            contentKey: shortid.generate(),
        };
        this.setLanguageFromUrl();

        this.remountPageComponents = this.remountPageComponents.bind(this);
    }

    componentDidMount() {
        if (LANDING_PAGES.includes(this.props.location.pathname)) {
            this.props.clearState();
        }

        storeKioskModeParameter();
        initReactFastclick();
        initPreventDoubleTapZoom();
        this.checkForSourceQueryParam();
        setArPageTitle(this.props.isExternalURLSourcePresent, 'Amplify Reading: Placement');
    }

    componentDidUpdate(prevProps) {
        if (this.props.location.search !== prevProps.location.search) {
            this.setLanguageFromUrl();
        }

        if (this.hasAuthenticated(prevProps) && !this.isAppDataFetched()) {
            this.props.fetchApplicationData();
        }

        if (this.shouldStartSecureBrowser(prevProps)) {
            if (isChromeOS()) {
                this.props.showErrorInChromeOSWithoutKiosk();
            } else {
                this.startSecureBrowser();
            }
        }

        if (this.hasAuthenticated(prevProps) && sessionStorage.getItem('reload_page')) {
            sessionStorage.removeItem('reload_page');
            window.location.reload();
        }
    }

    componentWillUnmount() {
        destroyPreventDoubleTapZoom();
    }

    // eslint-disable-next-line class-methods-use-this
    onFocusHandler() {
        // Should not allow student to pass assessments
        // decided to redirect to students home page
        sessionStorage.clear();
        window.location.assign(STUDENT_HOME);
    }

    startSecureBrowser() {
        this.setState({
            startingSecureBrowser: true,
        });
        window.addEventListener('focus', this.onFocusHandler);
        const {mazeSchoolId, currentSchoolyear} = this.props;
        // we use mazeSchoolId because this is only one measure which can be passed in SEB
        openSecureBrowser(`${SEB_URL}&school_id=${mazeSchoolId}&school_year=${currentSchoolyear}`, keycloak.token, keycloak.refreshToken);
    }

    shouldStartSecureBrowser() {
        return this.isAppDataFetched()
            && this.props.hasSecureAssessments
            && keycloak.token
            && keycloak.refreshToken
            && !this.state.startingSecureBrowser
            && !isRunningInSEBBrowser()
            && !isKioskChromeOS();
    }

    checkForSourceQueryParam() {
        const {
            source,
            benchmarkMode,
            benchmarkId,
            captions,
            arLanguage
        } = queryString.parse(this.props.location.search);
        if (source === "AR") {
            this.props.setExternalURLStatus();
            this.props.setBenchmarkModeStatus(benchmarkMode === "true");
            this.props.setArLanguage(arLanguage);
        }
        if (!isNil(benchmarkId)) {
            this.props.setBenchmarkId(benchmarkId);
        }
        if (captions === "true") {
            this.props.setShouldEnableCaptions();
        }
    };

    setLanguageFromUrl() {
        const { lang } = queryString.parse(this.props.location.search);
        // consider language in url only for assessment pages
        if (!LANDING_PAGES.includes(this.props.location.pathname) &&
            SUPPORTED_LANGUAGES.includes(lang)) {
            this.props.setCurrentLanguage(lang);
        }
    }

    isAppDataFetched() {
        return this.props.isPlaylistDataFetched
            && this.props.isResultDataFetched;
    }

    hasAuthenticated(prevProps) {
        return !prevProps.isLanternUser && this.props.isLanternUser;
    }

    hasPlaylistContent() {
        return this.props.isPlaylistDataFetched;
    }

    gradeLabel() {
        const gradeLevelMap = {
            KG: 'K',
            PK: 'Pre-K',
        };
        return this.props.studentGrade in gradeLevelMap
            ? gradeLevelMap[this.props.studentGrade]
            : parseInt(this.props.studentGrade, 10).toString();
    }

    headerTitle() {
        return `${this.props.studentName} - Grade ${this.gradeLabel()}`;
    }

    remountPageComponents() {
        this.setState({
            showContent: false,
            contentKey: shortid.generate(),
        });
        // Using setTimeout to add a delay before showing the content again.
        // This gives a chance for the children components to finish clearing the state.
        setTimeout(() => {
            this.setState({
                showContent: true,
            });
        }, 200);
    }

    shouldShowMainNavigationAndSubheader() {
        return PAGES_WITH_MAIN_NAVIGATION_AND_SUBHEADER.includes(this.props.location.pathname);
    }

    /*
        If we are at SEB -- show specific header with name only.
        If the logout button should be visible, the global navigation should be
        shown. Otherwise, the Lantern header appears instead.
    */

    displayNavigation() {
        if (this.shouldShowMainNavigationAndSubheader()) {
            return (
                <div className={classNames("navigation-container", { 'hide-all-nav-buttons': isRunningInSEBBrowser() || isKioskChromeOS() })}>
                    <AmpNavigationContainer
                        trackLogOut={this.props.trackLogOut}
                    />
                    <div className="subheader">
                        <div className="mclass-logo">
                            <span>mCLASS&#174;</span>
                        </div>
                        <div className="buttons-container">
                            <AccessibilitySettingsDropdown
                                isMazeLicensed={this.props.isMazeLicensed}
                            />
                            {
                                isRunningInSEBBrowser() &&
                                (
                                    <AmplifyButton oval variant="secondary" className="close-button" onClick={handleCloseSEB} icon="icon-v2-close">
                                        Close
                                    </AmplifyButton>
                                )
                            }
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <React.Fragment>
                <HeaderContainer
                    studentName={this.props.studentName}
                    studentGrade={this.gradeLabel()}
                    location={this.props.location}
                />
            </React.Fragment>
        );
    }

    renderAvatar() {
        if (this.props.isExternalURLSourcePresent) {
            return <Alex avatarConfig={{ animateAlex: true }} />;
        }
        return <AvatarContainer />;
    }

    pageContent() {
        return (
            <div>
                {this.hasPlaylistContent() && this.state.showContent
                    ? (
                        <div key={this.state.contentKey}>
                            {!this.props.isExternalURLSourcePresent && this.displayNavigation()}
                            {this.isAppDataFetched() && this.renderAvatar()}
                            {this.props.isResultDataFetched
                                ? this.props.isAvatarReadyToAnimate && this.props.children
                                : <LoadingPage />}
                            {this.props.captionsEnabled && <Captions />}
                            <LogoutModalContainer />
                            <SvgSprite />
                        </div>
                    ) : <LoadingPage />}
            </div>
        );
    }

    render() {
        return this.state.startingSecureBrowser ? (
            <LoadingPage />
        ) : (
            <div
                className={`lantern-container ${this.props.isExternalURLSourcePresent ? 'ar-skin' : ''} ${this.props.currentLanguage} ${this.context.fontSizeScaleValue ? `font-size-${this.context.fontSizeScaleValue}` : ''}
                 ${this.props.currentLanguage === LANGUAGES.ENGLISH && isAccessibilityEnabled() ? `zoom-in-${this.context.fontSizeScaleValue}` : ''}`}>
                {this.props.isExternalURLSourcePresent && <DeviceRotateAnimation />}
                {this.props.isLanternUser
                    ? this.pageContent()
                    : <SessionTrackerContainer />}
                <AppErrorContainer pageReloadCallback={this.remountPageComponents} />
                <AudioSavingErrorContainer />
            </div>
        );
    }
}

App.contextType = ApolloContext;

App.propTypes = {
    isPlaylistDataFetched: PropTypes.bool,
    isResultDataFetched: PropTypes.bool,
    children: PropTypes.node,
    studentName: PropTypes.string,
    studentGrade: PropTypes.string,
    isLanternUser: PropTypes.bool,
    isAvatarReadyToAnimate: PropTypes.bool.isRequired,
    fetchApplicationData: PropTypes.func.isRequired,
    clearState: PropTypes.func,
    location: PropTypes.object.isRequired,
    setCurrentLanguage: PropTypes.func,
    currentLanguage: PropTypes.string,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
    setExternalURLStatus: PropTypes.func.isRequired,
    setArLanguage: PropTypes.func.isRequired,
    setBenchmarkModeStatus: PropTypes.func.isRequired,
    trackLogOut: PropTypes.func,
    setBenchmarkId: PropTypes.func.isRequired,
    setShouldEnableCaptions: PropTypes.func.isRequired,
    captionsEnabled: PropTypes.bool.isRequired,
    hasSecureAssessments: PropTypes.bool.isRequired,
    currentSchoolyear: PropTypes.number,
    showErrorInChromeOSWithoutKiosk: PropTypes.func,
    mazeSchoolId: PropTypes.string,
    isMazeLicensed: PropTypes.bool,
};

App.defaultProps = {
    isPlaylistDataFetched: false,
    isResultDataFetched: false,
    children: undefined,
    studentName: '',
    studentGrade: '',
    isLanternUser: false,
    clearState: () => { },
    setCurrentLanguage: () => { },
    currentLanguage: '',
    trackLogOut: () => { },
    currentSchoolyear: 2024,
    showErrorInChromeOSWithoutKiosk: () => {},
    mazeSchoolId: '',
    isMazeLicensed: false,
};

export default App;
